/*
  The figures in the set and card page headers (Robert, 2026-10-20: make the set and card
  pages as modern as the game page, and "if there's any other information that should be
  added … let's try it"). Pure, so they are unit-tested; SetHeader and CardHeader only lay
  them out.

  **Two grade vocabularies.** A set's products carry `finalGradeCounts` keyed by the grade's
  VALUE ("Gold 10.0", "10.0", "Gem Mint 10.0", "9.5" … "0.0"); a card's gradings carry the
  grade's NAME ("Flawless", "Pristine", "Gem Mint 10", "Gem Mint" … "Authentic"). FinishTable
  learned this the hard way (its comment on "Flawless"). Both map here onto the same seven
  bands, so the set bar and the card bar mean the same thing.
*/

import { combineSetsByName } from "./sets";

export type GradeBandKey =
  | "flawless"
  | "pristine"
  | "gemMint10"
  | "nineFive"
  | "nine"
  | "lower"
  | "authentic";

/* Best first: the bar's order and the legend's. */
export const GRADE_BANDS: readonly { key: GradeBandKey; label: string }[] = [
  { key: "flawless", label: "Flawless 10" },
  { key: "pristine", label: "Pristine 10" },
  { key: "gemMint10", label: "Gem Mint 10" },
  { key: "nineFive", label: "9.5" },
  { key: "nine", label: "9" },
  { key: "lower", label: "8.5 & below" },
  { key: "authentic", label: "Authentic" },
];

export type GradeTally = Record<GradeBandKey, number>;

const emptyTally = (): GradeTally => ({
  flawless: 0,
  pristine: 0,
  gemMint10: 0,
  nineFive: 0,
  nine: 0,
  lower: 0,
  authentic: 0,
});

/* finalGradeCounts values. Anything 1.0–8.5 is "lower". */
const BY_VALUE: Record<string, GradeBandKey> = {
  "Gold 10.0": "flawless",
  "10.0": "pristine",
  "Gem Mint 10.0": "gemMint10",
  "9.5": "nineFive",
  "9.0": "nine",
  "0.0": "authentic",
};

/*
  Grading names, best first. The order is what "top grade" means on a card: the first name
  here that any grading carries. The labels are the ones the set table's columns use.
*/
export const GRADE_NAMES: readonly { name: string; value: string; label: string; band: GradeBandKey }[] = [
  { name: "Flawless", value: "Gold 10.0", label: "Flawless 10", band: "flawless" },
  { name: "Pristine", value: "10.0", label: "Pristine 10", band: "pristine" },
  { name: "Gem Mint 10", value: "Gem Mint 10.0", label: "Gem Mint 10", band: "gemMint10" },
  { name: "Gem Mint", value: "9.5", label: "Gem Mint 9.5", band: "nineFive" },
  { name: "Mint", value: "9.0", label: "Mint 9", band: "nine" },
  { name: "NM-Mint+", value: "8.5", label: "NM-Mint+ 8.5", band: "lower" },
  { name: "NM-Mint", value: "8.0", label: "NM-Mint 8", band: "lower" },
  { name: "Near Mint+", value: "7.5", label: "Near Mint+ 7.5", band: "lower" },
  { name: "Near Mint", value: "7.0", label: "Near Mint 7", band: "lower" },
  { name: "Excellent", value: "6.0", label: "Excellent 6", band: "lower" },
  { name: "Good", value: "5.0", label: "Good 5", band: "lower" },
  { name: "Fair", value: "4.0", label: "Fair 4", band: "lower" },
  { name: "Worn", value: "3.0", label: "Worn 3", band: "lower" },
  { name: "Poor", value: "2.0", label: "Poor 2", band: "lower" },
  { name: "Damaged", value: "1.0", label: "Damaged 1", band: "lower" },
  { name: "Authentic", value: "0.0", label: "Authentic", band: "authentic" },
];

const NAME_INDEX = new Map(GRADE_NAMES.map((entry, index) => [entry.name, index]));

export const bandForGradeValue = (grade: string | number): GradeBandKey | null => {
  /* A number arrives as 9, not "9.0". */
  const value = typeof grade === "number" ? grade.toFixed(1) : String(grade);
  if (BY_VALUE[value]) {
    return BY_VALUE[value];
  }
  const numeric = Number(value);
  return isFinite(numeric) && numeric >= 1 && numeric <= 8.5 ? "lower" : null;
};

export const bandForGradeName = (name: string | null | undefined): GradeBandKey | null => {
  const index = name ? NAME_INDEX.get(name) : undefined;
  return index === undefined ? null : GRADE_NAMES[index].band;
};

export interface GradeCountRow {
  finalGradeCounts?: { grade: string | number; count: number }[] | null;
}

/* A set's products, summed into bands. A grade value no band knows is left out. */
export const tallyGradeCounts = (rows: readonly GradeCountRow[]): GradeTally => {
  const tally = emptyTally();
  rows.forEach((row) => {
    (row.finalGradeCounts ?? []).forEach(({ grade, count }) => {
      const band = bandForGradeValue(grade);
      if (band && typeof count === "number" && isFinite(count) && count > 0) {
        tally[band] += count;
      }
    });
  });
  return tally;
};

/* A card's gradings, counted into bands. */
export const tallyGradings = (rows: readonly { grade?: string | null }[]): GradeTally => {
  const tally = emptyTally();
  rows.forEach((row) => {
    const band = bandForGradeName(row.grade);
    if (band) {
      tally[band] += 1;
    }
  });
  return tally;
};

export const tallyTotal = (tally: GradeTally): number =>
  GRADE_BANDS.reduce((sum, { key }) => sum + tally[key], 0);

/* Every 10: Flawless, Pristine and Gem Mint 10. */
export const tallyTens = (tally: GradeTally): number =>
  tally.flawless + tally.pristine + tally.gemMint10;

export interface GradeMixSegment {
  key: GradeBandKey;
  label: string;
  count: number;
  /* 0–100, of the tally's own total. */
  share: number;
}

/* The bands that have any grades, best first, each with its share. Empty for an empty tally. */
export const gradeMix = (tally: GradeTally): GradeMixSegment[] => {
  const total = tallyTotal(tally);
  if (total <= 0) {
    return [];
  }
  return GRADE_BANDS.filter(({ key }) => tally[key] > 0).map(({ key, label }) => ({
    key,
    label,
    count: tally[key],
    share: (tally[key] / total) * 100,
  }));
};

export interface Rank {
  /* 1-based; ties share the better place. */
  rank: number;
  of: number;
}

/*
  Where one item ranks by a count among items that have one. Null when the item is not
  there or has no count, so a header says nothing rather than a wrong place.
*/
export const rankByCount = <T>(
  items: readonly T[],
  isMine: (item: T) => boolean,
  countOf: (item: T) => number | null | undefined
): Rank | null => {
  const counted = items.filter((item) => {
    const count = countOf(item);
    return typeof count === "number" && isFinite(count) && count > 0;
  });
  const mine = counted.find(isMine);
  if (!mine) {
    return null;
  }
  const value = countOf(mine) as number;
  return {
    rank: 1 + counted.filter((item) => (countOf(item) as number) > value).length,
    of: counted.length,
  };
};

export interface RankableSet {
  setName: string;
  cardsGraded: number | null;
  language?: string | null;
}

/*
  A set's place in its game. Same-named rows are one set on the game page (a Japanese and a
  Korean row of "MEGA: Inferno X" are one tile, their counts summed), so they are one set
  here too, or the rank would disagree with the page the visitor just came from. The caller
  must pass the game's WHOLE catalogue: a partial one ranks against the wrong field.
*/
export const setRank = (sets: readonly RankableSet[], setName: string): Rank | null => {
  const key = setName.trim().toLowerCase();
  return rankByCount(
    combineSetsByName(sets),
    (set) => set.setName.trim().toLowerCase() === key,
    (set) => set.cardsGraded
  );
};

export interface SetCardRow extends GradeCountRow {
  cardsGraded: number | null;
}

export interface SetSummary {
  /* Sum of the cards' own totals: the figure the set's table rows add up to. */
  graded: number;
  /* Cards the catalogue lists in the set, and how many of them PCG has graded at all. */
  cards: number;
  cardsWithGrades: number;
  tally: GradeTally;
}

export const setSummary = (cards: readonly SetCardRow[]): SetSummary => ({
  graded: cards.reduce((sum, card) => sum + (card.cardsGraded ?? 0), 0),
  cards: cards.length,
  cardsWithGrades: cards.filter((card) => (card.cardsGraded ?? 0) > 0).length,
  tally: tallyGradeCounts(cards),
});

export interface GradingRow {
  grade?: string | null;
  finalGrade?: number | null;
  centeringGrade?: number | null;
  cornersGrade?: number | null;
  edgesGrade?: number | null;
  surfaceGrade?: number | null;
}

export interface SubgradeAverages {
  centering: number;
  corners: number;
  edges: number;
  surface: number;
  /* How many gradings the averages are over. */
  over: number;
}

export interface CardSummary {
  graded: number;
  tally: GradeTally;
  /* The best grade any grading of this card reached, and how many share it. Authentic is not a grade. */
  topGrade: { label: string; pop: number } | null;
  subgrades: SubgradeAverages | null;
}

const subgradeValue = (value: number | null | undefined): number | null =>
  typeof value === "number" && isFinite(value) && value > 0 ? value : null;

export const cardSummary = (gradings: readonly GradingRow[]): CardSummary => {
  let best: number | null = null;
  let pop = 0;
  gradings.forEach((row) => {
    const index = row.grade ? NAME_INDEX.get(row.grade) : undefined;
    if (index === undefined || GRADE_NAMES[index].band === "authentic") {
      return;
    }
    if (best === null || index < best) {
      best = index;
      pop = 1;
    } else if (index === best) {
      pop += 1;
    }
  });

  /* Only gradings with all four subgrades: an Authentic has none, and half a set skews the mean. */
  const scored = gradings
    .map((row) => [row.centeringGrade, row.cornersGrade, row.edgesGrade, row.surfaceGrade].map(subgradeValue))
    .filter((values): values is number[] => values.every((value) => value !== null));
  const mean = (column: number) =>
    scored.reduce((sum, values) => sum + values[column], 0) / scored.length;

  return {
    graded: gradings.length,
    tally: tallyGradings(gradings),
    topGrade: best === null ? null : { label: GRADE_NAMES[best].label, pop },
    subgrades:
      scored.length > 0
        ? { centering: mean(0), corners: mean(1), edges: mean(2), surface: mean(3), over: scored.length }
        : null,
  };
};

/*
  The best grade in a card's finalGradeCounts and how many share it: the same answer as
  cardSummary's topGrade, from the per-card counts the set endpoint carries. Those are
  complete, where a card's gradings arrive at most 200 at a time.
*/
export const topGradeFromCounts = (
  counts: GradeCountRow["finalGradeCounts"]
): { label: string; pop: number } | null => {
  const byValue = new Map<string, number>();
  (counts ?? []).forEach(({ grade, count }) => {
    const value = typeof grade === "number" ? grade.toFixed(1) : String(grade);
    if (typeof count === "number" && count > 0) {
      byValue.set(value, (byValue.get(value) ?? 0) + count);
    }
  });
  const best = GRADE_NAMES.find((entry) => entry.band !== "authentic" && byValue.has(entry.value));
  return best ? { label: best.label, pop: byValue.get(best.value) as number } : null;
};

/*
  A mean put back on PCG's own scale. Subgrades are only ever awarded in half steps, so an
  average of 9.3 names a grade PCG never gives (Robert, 2026-10-21: "we dont allocate those
  numbers"). Nearest 0.5, ties up: 9.2 -> 9, 9.25 -> 9.5, 9.7 -> 9.5, 9.75 -> 10.

  The epsilon keeps a true tie a tie: a mean of halves can land a hair under x.25 in
  floating point (9.249999…), which would otherwise round the wrong way.
*/
export const roundToHalfStep = (value: number): number => Math.round(value * 2 + 1e-9) / 2;

/* 9.3 -> "9.5", 9.2 -> "9.0". Every displayed average subgrade goes through here. */
export const formatSubgrade = (value: number): string => roundToHalfStep(value).toFixed(1);
